import { z } from 'zod';

export const ALERT_SEVERITIES = ['info', 'warning', 'critical'] as const;
export type AlertSeverity = (typeof ALERT_SEVERITIES)[number];

export const ALERT_STATES = ['firing', 'acknowledged', 'resolved', 'suppressed'] as const;
export type AlertState = (typeof ALERT_STATES)[number];

export const NOTIFY_CHANNELS = ['email', 'sms', 'webhook', 'push', 'inapp'] as const;
export type NotifyChannel = (typeof NOTIFY_CHANNELS)[number];

export const alertSchema = z.object({
  id: z.string(),
  /**
   * Stable identity of the *condition*, not the occurrence. Re-firing the same
   * dedupeKey updates the open alert instead of spawning a duplicate.
   */
  dedupeKey: z.string(),
  ruleId: z.string().nullable(),
  pluginId: z.string(),
  resourceId: z.string().nullable(),
  customerId: z.string().nullable(),
  severity: z.enum(ALERT_SEVERITIES),
  state: z.enum(ALERT_STATES),
  title: z.string(),
  description: z.string().default(''),
  /** Value that tripped the rule, for "CPU 94% > 90%" style messages. */
  value: z.number().nullable().default(null),
  threshold: z.number().nullable().default(null),
  labels: z.record(z.string()).default({}),
  /** How many times this condition has re-fired while open. */
  occurrences: z.number().int().default(1),
  firstSeenAt: z.number(),
  lastSeenAt: z.number(),
  acknowledgedAt: z.number().nullable().default(null),
  acknowledgedBy: z.string().nullable().default(null),
  resolvedAt: z.number().nullable().default(null),
  /** Ticket auto-opened from this alert, if any. */
  ticketId: z.string().nullable().default(null),
});
export type Alert = z.infer<typeof alertSchema>;

/**
 * A standing "stop telling me about this" order.
 *
 * Keyed on `dedupeKey` — the condition — rather than on an alert row, because
 * the point of suppressing is to stay quiet *after* the current alert resolves
 * and the same condition trips again. Suppressed conditions are still recorded
 * (occurrences keep climbing, history stays honest); they just never page
 * anyone and stay out of the open queue.
 */
export const alertSuppressionSchema = z.object({
  dedupeKey: z.string(),
  reason: z.string().default(''),
  createdAt: z.number(),
  createdBy: z.string().nullable().default(null),
  createdByName: z.string().default(''),
  /** null means "until someone lifts it". */
  expiresAt: z.number().nullable().default(null),
});
export type AlertSuppression = z.infer<typeof alertSuppressionSchema>;

export const COMPARATORS = ['gt', 'gte', 'lt', 'lte', 'eq', 'neq'] as const;
export type Comparator = (typeof COMPARATORS)[number];

export function compare(op: Comparator, a: number, b: number): boolean {
  switch (op) {
    case 'gt':
      return a > b;
    case 'gte':
      return a >= b;
    case 'lt':
      return a < b;
    case 'lte':
      return a <= b;
    case 'eq':
      return a === b;
    case 'neq':
      return a !== b;
  }
}

/**
 * Two rule shapes cover essentially everything we need:
 *  - `metric`: threshold on a metric over a window ("avg cpu.usage > 90 for 5m")
 *  - `health`: resource entered a health state and stayed there ("down for 2m")
 * Plugins can also raise alerts directly through the SDK for things that do not
 * fit either shape (e.g. "firewall config changed").
 */
export const alertRuleSchema = z.object({
  id: z.string(),
  name: z.string().min(1),
  enabled: z.boolean().default(true),
  pluginId: z.string().nullable().default(null),
  /** Restrict to resources matching these selectors. Empty = all. */
  selector: z
    .object({
      resourceIds: z.array(z.string()).default([]),
      kinds: z.array(z.string()).default([]),
      tags: z.array(z.string()).default([]),
      customerIds: z.array(z.string()).default([]),
    })
    .default({ resourceIds: [], kinds: [], tags: [], customerIds: [] }),
  type: z.enum(['metric', 'health']),
  severity: z.enum(ALERT_SEVERITIES).default('warning'),

  // metric rules
  metric: z.string().nullable().default(null),
  agg: z.enum(['avg', 'min', 'max', 'sum', 'last']).default('avg'),
  comparator: z.enum(COMPARATORS).default('gt'),
  threshold: z.number().nullable().default(null),

  // health rules
  healthState: z.enum(['down', 'degraded', 'unknown']).nullable().default(null),

  /** Condition must hold this long before the alert fires. Debounces flapping. */
  forSec: z.number().int().min(0).default(120),
  /** Do not re-notify more often than this while the alert stays open. */
  renotifySec: z.number().int().min(0).default(3600),
  channels: z.array(z.enum(NOTIFY_CHANNELS)).default(['inapp', 'email', 'push']),
  /** Notification policy id controlling who gets paged and when to escalate. */
  policyId: z.string().nullable().default(null),
  /** Auto-open a ticket when this fires. */
  autoTicket: z.boolean().default(false),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type AlertRule = z.infer<typeof alertRuleSchema>;

export const alertRuleCreateSchema = alertRuleSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type AlertRuleCreate = z.infer<typeof alertRuleCreateSchema>;

/** Who to notify, on which channels, with time-based escalation. */
export const notificationPolicySchema = z.object({
  id: z.string(),
  name: z.string(),
  /** Escalation ladder: step 0 fires immediately, later steps after delayMin. */
  steps: z
    .array(
      z.object({
        delayMin: z.number().int().min(0).default(0),
        userIds: z.array(z.string()).default([]),
        /** Direct addresses for people who are not portal users. */
        emails: z.array(z.string()).default([]),
        phones: z.array(z.string()).default([]),
        channels: z.array(z.enum(NOTIFY_CHANNELS)).default(['email']),
      }),
    )
    .default([]),
  /** Outside these hours only `critical` alerts page. Server local time. */
  quietHours: z
    .object({
      enabled: z.boolean().default(false),
      startHour: z.number().int().min(0).max(23).default(22),
      endHour: z.number().int().min(0).max(23).default(7),
    })
    .default({ enabled: false, startHour: 22, endHour: 7 }),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type NotificationPolicy = z.infer<typeof notificationPolicySchema>;

export interface NotificationResult {
  channel: NotifyChannel;
  target: string;
  ok: boolean;
  error?: string;
  providerId?: string;
}
